import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { CSSProperties } from 'react';
import {
    CpmCheckbox,
    CpmEmptyState,
    CpmFilterDropdown,
    CpmSortIcon,
    CpmStatusChip,
} from '../../../../components/design-system';
import {
    CPM_CONTAINER_TYPE_OPTIONS,
    cpmLicenseTypeOptions,
    formatCpmCount,
    formatVolumeLabel,
} from '../../../../constants/centralProductMaster.constants';
import type {
    CpmBrandRecord,
    CpmCatalogView,
    CpmMasterArea,
    CpmMasterRecord,
    CpmProductRecord,
    CpmStateProductRecord,
    CpmVariantNested,
    CpmVariantRecord,
    CpmLicenseTypesByState,
} from '../../../../types/centralProductMaster.types';

interface MasterRecordTableProps {
    area: CpmMasterArea;
    catalogView: CpmCatalogView;
    items: CpmMasterRecord[];
    selectedIds: string[];
    collapsedIds: string[];
    bulkMode: boolean;
    bulkDrafts: Record<string, Record<string, unknown>>;
    sortBy: string;
    sortDir: 'asc' | 'desc';
    childSortKey: string;
    childSortDir: 'asc' | 'desc';
    onToggle: (record: CpmMasterRecord | CpmVariantNested, nested?: boolean) => void;
    onToggleAll: (checked: boolean) => void;
    onToggleExpand: (id: string) => void;
    onOpen: (level: string, id: string) => void;
    onViewProducts: (brandId: string, brandName: string) => void;
    onApprove: (level: string, id: string, name: string, status: string) => void;
    onCreateVariant: (productId: string, productName: string) => void;
    onBulkField: (key: string, field: string, value: unknown) => void;
    onSort: (key: string) => void;
    onChildSort: (key: string) => void;
    licenseTypesByState?: CpmLicenseTypesByState;
    licenseTypesReady?: boolean;
}

type ApproveState = 'hidden' | 'active' | 'blocked';

function canApproveStatus(status: string) {
    return status === 'PENDING' || status === 'NEEDS_REVIEW';
}

function isParentActive(parentStatus?: string) {
    return parentStatus === undefined || parentStatus === 'ACTIVE';
}

function resolveApproveState(ownStatus: string, parentStatus?: string): ApproveState {
    if (!canApproveStatus(ownStatus)) return 'hidden';
    if (!isParentActive(parentStatus)) return 'blocked';
    return 'active';
}

function draftKey(level: string, id: string) {
    return `${level}:${id}`;
}

function isEdited(
    bulkMode: boolean,
    bulkDrafts: Record<string, Record<string, unknown>>,
    key: string
) {
    return bulkMode && Object.keys(bulkDrafts[key] ?? {}).length > 0;
}

function formatMoney(n: number | null | undefined) {
    if (n == null) return '—';
    return `₹${formatCpmCount(n)}`;
}

function rowSurfaceStyle(opts: {
    selected: boolean;
    bulkMode: boolean;
    edited: boolean;
    warn?: boolean;
    nested?: boolean;
}): CSSProperties {
    const { selected, bulkMode, edited, warn = false, nested = false } = opts;
    let background = '#fff';

    if (bulkMode && edited) {
        background = 'rgba(110,100,240,.05)';
    } else if (selected) {
        background = 'rgba(110,100,240,.06)';
    } else if (warn && !bulkMode) {
        background = 'var(--cpm-warning-bg)';
    } else if (nested) {
        background = 'var(--cpm-surface-subtle)';
    }

    const style: CSSProperties = {
        background,
        borderBottom: '1px solid var(--cpm-border-default)',
    };

    if (bulkMode && edited) {
        style.boxShadow = 'inset 3px 0 0 var(--cpm-brand-purple)';
    }

    return style;
}

function compareStrings(a: string | null | undefined, b: string | null | undefined) {
    return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });
}

function compareNumbers(a: number | null | undefined, b: number | null | undefined) {
    return (a ?? 0) - (b ?? 0);
}

/** Design Master Editor.dc.html cellText for aliases count columns. */
function formatAliasCountLabel(n: number): string {
    if (n === 0) return 'No aliases';
    return `${formatCpmCount(n)} ${n === 1 ? 'alias' : 'aliases'}`;
}

function usesApiSort(area: CpmMasterArea, catalogView: CpmCatalogView, sortBy: string) {
    if (area === 'brands') {
        return (
            sortBy === 'name' ||
            sortBy === 'aliases' ||
            sortBy === 'countryOfOrigin' ||
            sortBy === 'parentCompany' ||
            sortBy === 'status' ||
            sortBy === 'products' ||
            sortBy === 'createdAt'
        );
    }
    if (area === 'stateProducts') {
        return (
            sortBy === 'state' ||
            sortBy === 'variant' ||
            sortBy === 'excise' ||
            sortBy === 'subcategory' ||
            sortBy === 'license' ||
            sortBy === 'purchasePrice' ||
            sortBy === 'retailPrice' ||
            sortBy === 'wholesalePrice' ||
            sortBy === 'distilleryPrice' ||
            sortBy === 'status' ||
            sortBy === 'createdAt'
        );
    }
    if (area === 'catalog' && catalogView === 'variants') {
        return (
            sortBy === 'name' ||
            sortBy === 'product' ||
            sortBy === 'volume' ||
            sortBy === 'container' ||
            sortBy === 'itemPerCase' ||
            sortBy === 'barcodes' ||
            sortBy === 'aliases' ||
            sortBy === 'status' ||
            sortBy === 'createdAt'
        );
    }
    // Catalog products / combined parent rows
    return (
        sortBy === 'name' ||
        sortBy === 'aliases' ||
        sortBy === 'brand' ||
        sortBy === 'variants' ||
        sortBy === 'alcoholContent' ||
        sortBy === 'status' ||
        sortBy === 'createdAt'
    );
}

function sortVariants(
    variants: CpmVariantNested[],
    key: string,
    dir: 'asc' | 'desc'
): CpmVariantNested[] {
    if (!key) return variants;
    const sorted = [...variants].sort((a, b) => {
        let cmp = 0;
        switch (key) {
            case 'name':
                cmp = compareStrings(a.name, b.name);
                break;
            case 'volume':
                cmp = compareNumbers(a.volumeMl, b.volumeMl);
                break;
            case 'container':
                cmp = compareStrings(a.containerType, b.containerType);
                break;
            case 'itemPerCase':
                cmp = compareNumbers(a.itemPerCase, b.itemPerCase);
                break;
            case 'barcodes':
                cmp = compareNumbers(a.gtin?.length ?? 0, b.gtin?.length ?? 0);
                break;
            case 'aliases':
                cmp = compareNumbers(a.aliases?.length ?? 0, b.aliases?.length ?? 0);
                break;
            case 'status':
                cmp = compareStrings(a.status, b.status);
                break;
            default:
                cmp = 0;
        }
        return dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
}

function clientSortValue(
    area: CpmMasterArea,
    catalogView: CpmCatalogView,
    row: CpmMasterRecord,
    sortBy: string
): string | number {
    if (area === 'brands') {
        const r = row as CpmBrandRecord;
        switch (sortBy) {
            case 'aliases':
                return r.aliases?.length ?? 0;
            case 'countryOfOrigin':
                return r.countryOfOrigin ?? '';
            case 'parentCompany':
                return r.parentCompany ?? '';
            case 'products':
                return r.productCount ?? 0;
            case 'createdAt':
                return r.createdAt ?? '';
            default:
                return ((r as unknown as Record<string, unknown>)[sortBy] as string | number) ?? '';
        }
    }

    if (area === 'stateProducts') {
        const r = row as CpmStateProductRecord;
        switch (sortBy) {
            case 'variant':
                return r.variantInfo?.name ?? '';
            case 'excise':
                return r.exciseCategoryName ?? '';
            case 'subcategory':
                return r.exciseSubCategoryName ?? '';
            case 'license':
                return (r.licenseType ?? []).join(', ');
            case 'purchasePrice':
                return r.purchasePrice ?? 0;
            case 'retailPrice':
                return r.retailPrice ?? 0;
            case 'wholesalePrice':
                return r.wholesalePrice ?? 0;
            case 'distilleryPrice':
                return r.distilleryPrice ?? 0;
            default:
                return ((r as unknown as Record<string, unknown>)[sortBy] as string | number) ?? '';
        }
    }

    if (catalogView === 'variants') {
        const r = row as CpmVariantRecord;
        switch (sortBy) {
            case 'product':
                return [r.productInfo?.name, r.brandInfo?.name].filter(Boolean).join(' · ');
            case 'volume':
                return r.volumeMl ?? 0;
            case 'container':
                return r.containerType ?? '';
            case 'itemPerCase':
                return r.itemPerCase ?? 0;
            case 'barcodes':
                return r.gtin?.length ?? 0;
            default:
                return ((r as unknown as Record<string, unknown>)[sortBy] as string | number) ?? '';
        }
    }

    const r = row as CpmProductRecord;
    switch (sortBy) {
        case 'brand':
            return r.brandInfo?.name ?? '';
        case 'variants':
            return r.variants?.length ?? r.variantCount ?? 0;
        case 'alcoholContent':
            return r.alcoholContent ?? 0;
        default:
            return ((r as unknown as Record<string, unknown>)[sortBy] as string | number) ?? '';
    }
}

function clientSortItems(
    area: CpmMasterArea,
    catalogView: CpmCatalogView,
    items: CpmMasterRecord[],
    sortBy: string,
    sortDir: 'asc' | 'desc'
): CpmMasterRecord[] {
    if (usesApiSort(area, catalogView, sortBy)) return items;

    return [...items].sort((a, b) => {
        const av = clientSortValue(area, catalogView, a, sortBy);
        const bv = clientSortValue(area, catalogView, b, sortBy);
        let cmp = 0;
        if (typeof av === 'number' && typeof bv === 'number') {
            cmp = av - bv;
        } else {
            cmp = compareStrings(String(av), String(bv));
        }
        return sortDir === 'asc' ? cmp : -cmp;
    });
}

/** Design table header ink — Counter Flow `--text-primary` / active purple. */
const TH_COLOR = '#1A1A1A';
const TH_ACTIVE = '#6E64F0';
const TH_ARROW_IDLE = '#717182';

function SortableHead({
    label,
    sortKey,
    activeKey,
    dir,
    onSort,
    child = false,
    className = '',
    style,
}: {
    label: string;
    sortKey: string;
    activeKey: string;
    dir: 'asc' | 'desc';
    onSort: (key: string) => void;
    child?: boolean;
    className?: string;
    style?: CSSProperties;
}) {
    const active = activeKey === sortKey;
    const ink = active ? TH_ACTIVE : TH_COLOR;
    return (
        <button
            type="button"
            onClick={() => onSort(sortKey)}
            title={child ? 'Sort the variants in every product' : 'Sort by this column'}
            className={[
                'cpm-th inline-flex items-center justify-self-start border-0 bg-transparent p-0 uppercase cursor-pointer',
                child ? 'cpm-th-child' : '',
                active ? 'cpm-th-active' : '',
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            style={{
                // Explicit px — never `font: inherit` (that pulled 14px body / primary black).
                fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                fontSize: child ? 9.5 : 10,
                fontWeight: 800,
                letterSpacing: '0.08em',
                lineHeight: 1.3,
                textTransform: 'uppercase',
                color: ink,
                WebkitFontSmoothing: 'antialiased',
                ...style,
            }}
        >
            <span style={{ display: 'inline', whiteSpace: 'normal', color: ink }}>{label}</span>
            <span
                className="inline-flex items-center shrink-0"
                style={{
                    marginLeft: 4,
                    verticalAlign: -2,
                    color: active ? TH_ACTIVE : TH_ARROW_IDLE,
                    opacity: 1,
                }}
            >
                <CpmSortIcon active={active} dir={dir} child={child} />
            </span>
        </button>
    );
}

function GtinChips({ values }: { values?: string[] }) {
    const all = values ?? [];
    if (all.length === 0) {
        return (
            <span className="text-[12px] font-bold text-[var(--cpm-text-tertiary)]">No GTIN</span>
        );
    }
    const shown = all.slice(0, 5);
    return (
        <div className="flex flex-wrap items-center" style={{ gap: 5 }}>
            {shown.map((g) => (
                <span
                    key={g}
                    className="inline-flex items-center rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[var(--cpm-text-secondary)] tabular whitespace-nowrap"
                    style={{
                        padding: '2px 8px',
                        fontSize: 10.5,
                        fontWeight: 700,
                    }}
                >
                    {g}
                </span>
            ))}
            {all.length > shown.length ? (
                <span
                    className="text-[var(--cpm-text-tertiary)]"
                    style={{ fontSize: 10.5, fontWeight: 700 }}
                >
                    +{all.length - shown.length} more
                </span>
            ) : null}
        </div>
    );
}

function InlineText({
    value,
    onChange,
    placeholder,
    changed = false,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    changed?: boolean;
}) {
    return (
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className={[
                'w-full h-[34px] px-[10px] rounded-[8px] border-[1.5px] text-[12.5px] font-bold text-[var(--cpm-text-primary)] outline-none transition-all duration-150',
                changed
                    ? 'border-[var(--cpm-brand-purple)] bg-[rgba(110,100,240,.10)] shadow-[0_0_0_2px_rgba(110,100,240,.16)]'
                    : 'border-[var(--cpm-border-strong)] bg-white',
            ].join(' ')}
        />
    );
}

function InlineEnumCell({
    value,
    options,
    onChange,
    changed = false,
}: {
    value: string;
    options: readonly { value: string; label: string }[];
    onChange: (v: string) => void;
    changed?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: globalThis.MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const label = (options.find((o) => o.value === value)?.label ?? value) || '—';
    const highlight = changed || open;

    return (
        <div ref={rootRef} className="relative w-full" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={[
                    'w-full inline-flex items-center rounded-[8px] border-[1.5px] text-left transition-all duration-150',
                    highlight
                        ? 'border-[var(--cpm-brand-purple)] bg-[rgba(110,100,240,.10)]'
                        : 'border-[var(--cpm-border-strong)] bg-white',
                ].join(' ')}
                style={{ height: 34, padding: '0 10px', gap: 6 }}
            >
                <span className="flex-1 min-w-0 truncate text-[12.5px] font-bold text-[var(--cpm-text-primary)]">
                    {label}
                </span>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-text-tertiary)"
                    strokeWidth="2.4"
                    className="shrink-0"
                >
                    <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open ? (
                <div
                    className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-[var(--cpm-border-default)] rounded-[10px] shadow-[var(--cpm-shadow-dropdown)] p-[5px] flex flex-col max-h-[220px] overflow-y-auto"
                    style={{ minWidth: 160 }}
                >
                    {options.map((opt) => {
                        const active = opt.value === value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                    onChange(opt.value);
                                    setOpen(false);
                                }}
                                className={[
                                    'flex items-center gap-2 px-[10px] py-[7px] rounded-[8px] text-left',
                                    active
                                        ? 'text-[var(--cpm-brand-purple)]'
                                        : 'text-[var(--cpm-text-primary)] hover:bg-[var(--cpm-surface-subtle)]',
                                ].join(' ')}
                                style={{ fontSize: 12.5, fontWeight: active ? 800 : 700 }}
                            >
                                <span className="flex-1">{opt.label}</span>
                                {active ? (
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="var(--cpm-brand-purple)"
                                        strokeWidth="3"
                                    >
                                        <path
                                            d="M20 6 9 17l-5-5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function InlineChipCell({
    values,
    onChange,
    placeholder,
    addLabel,
    emptyLabel = 'None yet',
    changed = false,
}: {
    values: string[];
    onChange: (next: string[]) => void;
    placeholder: string;
    addLabel: string;
    emptyLabel?: string;
    changed?: boolean;
}) {
    const [adding, setAdding] = useState(false);
    const [text, setText] = useState('');

    const commit = () => {
        const v = text.trim();
        if (v && !values.includes(v)) onChange([...values, v]);
        setText('');
        setAdding(false);
    };

    return (
        <div
            className={[
                'flex flex-wrap items-center w-full rounded-[8px] border-[1.5px] transition-all duration-150',
                changed
                    ? 'border-[var(--cpm-brand-purple)] bg-[rgba(110,100,240,.10)] shadow-[0_0_0_2px_rgba(110,100,240,.16)]'
                    : 'border-[var(--cpm-border-strong)] bg-white',
            ].join(' ')}
            style={{ gap: 5, minHeight: 34, padding: '4px 6px', boxSizing: 'border-box' }}
            onClick={(e) => e.stopPropagation()}
        >
            {values.map((v) => (
                <span
                    key={v}
                    className="inline-flex items-center gap-[4px] rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)] max-w-full"
                    style={{ padding: '3px 4px 3px 9px', fontSize: 11, fontWeight: 800 }}
                >
                    <span className="max-w-[150px] truncate">{v}</span>
                    <button
                        type="button"
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--cpm-surface-skeleton)] text-[var(--cpm-text-secondary)]"
                        onClick={() => onChange(values.filter((x) => x !== v))}
                    >
                        <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.4"
                        >
                            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                    </button>
                </span>
            ))}
            {values.length === 0 && !adding ? (
                <span className="text-[11.5px] font-bold text-[var(--cpm-text-tertiary)] pl-[3px]">
                    {emptyLabel}
                </span>
            ) : null}
            {adding ? (
                <input
                    autoFocus
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            commit();
                        }
                        if (e.key === 'Escape') {
                            setAdding(false);
                            setText('');
                        }
                    }}
                    onBlur={commit}
                    placeholder={placeholder}
                    className="flex-1 min-w-[80px] h-[28px] px-[8px] rounded-[8px] border-[1.5px] border-[var(--cpm-brand-purple)] bg-white text-[12px] font-bold text-[var(--cpm-text-primary)] outline-none"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="inline-flex items-center gap-[4px] rounded-full border-[1.5px] border-dashed border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                    style={{ padding: '3px 9px', fontSize: 11, fontWeight: 800 }}
                >
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                    >
                        <path d="M5 12h14M12 5v14" strokeLinecap="round" />
                    </svg>
                    {addLabel}
                </button>
            )}
        </div>
    );
}

function ExpandCaret({
    expanded,
    onClick,
}: {
    expanded: boolean;
    onClick: (e: MouseEvent) => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="shrink-0 inline-flex items-center justify-center"
            style={{
                width: 22,
                height: 22,
                borderRadius: 7,
                border: '1.5px solid',
                borderColor: expanded ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)',
                background: expanded ? 'var(--cpm-purple-tint-13)' : '#fff',
                color: expanded ? 'var(--cpm-brand-purple)' : 'var(--cpm-text-tertiary)',
                transform: expanded ? 'rotate(90deg)' : undefined,
                transition: 'transform 150ms ease, border-color 150ms ease, background 150ms ease',
            }}
        >
            <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                className="shrink-0"
                style={{ maxWidth: 'none', display: 'block' }}
                aria-hidden
            >
                <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </button>
    );
}

function RowActions({
    show,
    approveState,
    onApprove,
    onEdit,
    align = 'end',
}: {
    show: boolean;
    approveState: ApproveState;
    onApprove: () => void;
    onEdit: () => void;
    /** `start` keeps Edit tight against the previous column (brands table). */
    align?: 'start' | 'end';
}) {
    if (!show) return <span />;
    return (
        <div
            className={[
                'flex items-center gap-[7px]',
                align === 'end' ? 'justify-self-end' : 'justify-self-start',
            ].join(' ')}
            onClick={(e) => e.stopPropagation()}
        >
            {approveState === 'active' ? (
                <button
                    type="button"
                    onClick={onApprove}
                    className="inline-flex items-center gap-[5px] px-[11px] py-[5px] rounded-full border-[1.5px] border-[var(--cpm-success-border)] bg-[var(--cpm-success-bg)] text-[11.5px] font-extrabold text-[var(--cpm-success-text-light)] whitespace-nowrap hover:brightness-[0.97]"
                >
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.8"
                        className="shrink-0"
                        style={{ maxWidth: 'none', display: 'block' }}
                        aria-hidden
                    >
                        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Approve
                </button>
            ) : approveState === 'blocked' ? (
                <button
                    type="button"
                    disabled
                    title="Parent must be Active first"
                    className="inline-flex items-center gap-[5px] px-[11px] py-[5px] rounded-full border-[1.5px] border-[var(--cpm-success-border)] bg-[var(--cpm-success-bg)] text-[11.5px] font-extrabold text-[var(--cpm-success-text-light)] whitespace-nowrap opacity-50 cursor-not-allowed"
                >
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.8"
                        className="shrink-0"
                        style={{ maxWidth: 'none', display: 'block' }}
                        aria-hidden
                    >
                        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Approve
                </button>
            ) : null}
            <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-[6px] px-[11px] py-[5px] rounded-full border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[11.5px] font-extrabold text-[var(--cpm-text-secondary)] whitespace-nowrap hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
            >
                Edit
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    className="shrink-0"
                    style={{ maxWidth: 'none', display: 'block' }}
                    aria-hidden
                >
                    <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
}

const headClass =
    'cpm-table-head grid items-end bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)] sticky top-0 z-[14] shadow-[inset_0_-1px_0_var(--cpm-border-default)]';

/** Shared sticky table-head type — mirrors Master Editor.dc.html `headRowStyle`. */
const headRowTypeStyle = {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: TH_COLOR,
    fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
    WebkitFontSmoothing: 'antialiased' as const,
};

export default function MasterRecordTable({
    area,
    catalogView,
    items,
    selectedIds,
    collapsedIds,
    bulkMode,
    bulkDrafts,
    sortBy,
    sortDir,
    childSortKey,
    childSortDir,
    onToggle,
    onToggleAll,
    onToggleExpand,
    onOpen,
    onViewProducts,
    onApprove,
    onCreateVariant,
    onBulkField,
    onSort,
    onChildSort,
    licenseTypesByState = {},
    licenseTypesReady = false,
}: MasterRecordTableProps) {
    const sortedItems = useMemo(
        () => clientSortItems(area, catalogView, items, sortBy, sortDir),
        [area, catalogView, items, sortBy, sortDir]
    );

    const allSelected = items.length > 0 && items.every((i) => selectedIds.includes(i._id));
    const combined = area === 'catalog' && catalogView === 'combined';

    if (items.length === 0) {
        return (
            <CpmEmptyState
                title="No records match"
                description="Loosen the filters or clear the search to see the rest of this level."
            />
        );
    }

    if (area === 'brands') {
        const rows = sortedItems as CpmBrandRecord[];
        // Design column tracks (Master Editor.dc.html cols.brands): name/aliases/country/
        // parent/status/products use their `w` (non-bulk) widths; bulk mode uses `bw`.
        // Actions is a fixed 152px column, right-aligned, same as every other level.
        const cols = bulkMode
            ? 'minmax(200px,1.2fr) minmax(250px,1.55fr) 160px minmax(180px,1fr) 138px 132px'
            : '40px minmax(220px,1.35fr) 136px 150px minmax(160px,1fr) 138px 132px 152px';
        // Design only right-aligns numeric brand columns (aliases/products), padding-right 22px.
        // Country/parent are plain left-aligned text — never centered.
        const numHead = '!justify-self-end text-right';
        return (
            <div className="w-full" style={{ minWidth: bulkMode ? 1180 : 1180 }}>
                <div
                    className={headClass}
                    style={{
                        gridTemplateColumns: cols,
                        gap: 10,
                        padding: '11px 18px',
                        ...headRowTypeStyle,
                    }}
                >
                    {!bulkMode ? (
                        <CpmCheckbox
                            checked={allSelected}
                            onChange={(e) => onToggleAll(e.target.checked)}
                        />
                    ) : null}
                    <SortableHead
                        label="Name"
                        sortKey="name"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Aliases"
                        sortKey="aliases"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className={bulkMode ? 'w-full' : numHead}
                        style={bulkMode ? undefined : { paddingRight: 22 }}
                    />
                    <SortableHead
                        label="Country of origin"
                        sortKey="countryOfOrigin"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Parent company"
                        sortKey="parentCompany"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Status"
                        sortKey="status"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Products"
                        sortKey="products"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className={numHead}
                        style={{ paddingRight: 22 }}
                    />
                    {!bulkMode ? <span /> : null}
                </div>
                {rows.map((row) => {
                    const selected = selectedIds.includes(row._id);
                    const key = draftKey('brand', row._id);
                    const draft = bulkDrafts[key] ?? {};
                    const edited = isEdited(bulkMode, bulkDrafts, key);
                    const name = bulkMode ? String(draft.name ?? row.name) : row.name;
                    const aliases = bulkMode
                        ? ((draft.aliases as string[] | undefined) ?? row.aliases ?? [])
                        : (row.aliases ?? []);
                    const productCount = row.productCount ?? 0;
                    const productCountLabel = `${formatCpmCount(productCount)} ${
                        productCount === 1 ? 'product' : 'products'
                    }`;
                    return (
                        <div
                            key={row._id}
                            className={[
                                'grid items-center w-full',
                                bulkMode ? 'cursor-default' : 'cursor-pointer',
                            ].join(' ')}
                            style={{
                                ...rowSurfaceStyle({ selected, bulkMode, edited }),
                                gridTemplateColumns: cols,
                                gap: 10,
                                padding: bulkMode ? '10px 18px' : '14px 18px',
                            }}
                            onClick={() => !bulkMode && onOpen('brand', row._id)}
                        >
                            {!bulkMode ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <CpmCheckbox
                                        checked={selected}
                                        onChange={() => onToggle(row)}
                                    />
                                </div>
                            ) : null}
                            <div className="min-w-0">
                                {bulkMode ? (
                                    <InlineText
                                        value={name}
                                        onChange={(v) => onBulkField(key, 'name', v)}
                                        changed={'name' in draft}
                                    />
                                ) : (
                                    <span className="text-[13.5px] font-extrabold tracking-[-0.01em] text-[var(--cpm-text-primary)]">
                                        {row.name}
                                    </span>
                                )}
                            </div>
                            <div
                                className={bulkMode ? 'min-w-0' : 'min-w-0 flex justify-end'}
                                style={bulkMode ? undefined : { paddingRight: 22 }}
                            >
                                {bulkMode ? (
                                    <InlineChipCell
                                        values={aliases}
                                        onChange={(v) => onBulkField(key, 'aliases', v)}
                                        placeholder="Alias, then Enter"
                                        addLabel="Add"
                                        emptyLabel="No aliases"
                                        changed={'aliases' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {formatCpmCount(row.aliases?.length ?? 0)}
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0 overflow-hidden">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(
                                            draft.countryOfOrigin ?? row.countryOfOrigin ?? ''
                                        )}
                                        onChange={(v) => onBulkField(key, 'countryOfOrigin', v)}
                                        changed={'countryOfOrigin' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere]">
                                        {row.countryOfOrigin || '—'}
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0 overflow-hidden">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(
                                            draft.parentCompany ?? row.parentCompany ?? ''
                                        )}
                                        onChange={(v) => onBulkField(key, 'parentCompany', v)}
                                        changed={'parentCompany' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere]">
                                        {row.parentCompany || '—'}
                                    </span>
                                )}
                            </div>
                            <CpmStatusChip status={row.status} />
                            {!bulkMode && productCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewProducts(row._id, row.name);
                                    }}
                                    className="min-w-0 flex justify-end bg-transparent border-0 p-0 cursor-pointer text-[12.5px] font-semibold text-[var(--cpm-text-secondary)] tabular whitespace-nowrap hover:text-[var(--cpm-brand-purple)] hover:underline hover:underline-offset-[3px] hover:decoration-[var(--cpm-brand-purple)]"
                                    style={{ paddingRight: 22 }}
                                >
                                    {productCountLabel}
                                </button>
                            ) : (
                                <span
                                    className="min-w-0 flex justify-end text-[12.5px] font-semibold text-[var(--cpm-text-secondary)] tabular whitespace-nowrap"
                                    style={{ paddingRight: 22 }}
                                >
                                    {productCountLabel}
                                </span>
                            )}
                            {!bulkMode ? (
                                <RowActions
                                    show
                                    align="end"
                                    approveState={resolveApproveState(row.status)}
                                    onApprove={() =>
                                        onApprove('brand', row._id, row.name, row.status)
                                    }
                                    onEdit={() => onOpen('brand', row._id)}
                                />
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
    }

    if (area === 'stateProducts') {
        const rows = sortedItems as CpmStateProductRecord[];
        const cols =
            '40px 96px minmax(180px,1.5fr) 92px 116px minmax(118px,1fr) 78px 74px 80px 82px 88px 152px';
        return (
            <div style={{ minWidth: 1400 }}>
                <div
                    className={headClass}
                    style={{
                        gridTemplateColumns: cols,
                        gap: 10,
                        padding: '11px 18px',
                        ...headRowTypeStyle,
                    }}
                >
                    {!bulkMode ? (
                        <CpmCheckbox
                            checked={allSelected}
                            onChange={(e) => onToggleAll(e.target.checked)}
                        />
                    ) : (
                        <span />
                    )}
                    <SortableHead
                        label="State"
                        sortKey="state"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Variant"
                        sortKey="variant"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Excise category"
                        sortKey="excise"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Subcategory"
                        sortKey="subcategory"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="License type"
                        sortKey="license"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Purchase price"
                        sortKey="purchasePrice"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className="justify-end w-full"
                    />
                    <SortableHead
                        label="Retail price"
                        sortKey="retailPrice"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className="justify-end w-full"
                    />
                    <SortableHead
                        label="Wholesale price"
                        sortKey="wholesalePrice"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className="justify-end w-full"
                    />
                    <SortableHead
                        label="Distillery price"
                        sortKey="distilleryPrice"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className="justify-end w-full"
                    />
                    <SortableHead
                        label="Status"
                        sortKey="status"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <span />
                </div>
                {rows.map((row) => {
                    const selected = selectedIds.includes(row._id);
                    const key = draftKey('stateProduct', row._id);
                    const draft = bulkDrafts[key] ?? {};
                    const edited = isEdited(bulkMode, bulkDrafts, key);
                    const missingExcise = !row.exciseCategory && !row.exciseCategoryName;
                    const licenseValues = bulkMode
                        ? ((draft.licenseType as string[] | undefined) ?? row.licenseType ?? [])
                        : (row.licenseType ?? []);
                    const rowLicenseTypes = licenseTypesByState[row.state];
                    const rowLicenseEmpty =
                        licenseTypesReady &&
                        (rowLicenseTypes == null || rowLicenseTypes.length === 0);
                    const rowLicenseDisabled = !licenseTypesReady || rowLicenseEmpty;
                    return (
                        <div
                            key={row._id}
                            className="grid items-center cursor-pointer"
                            style={{
                                ...rowSurfaceStyle({
                                    selected,
                                    bulkMode,
                                    edited,
                                    warn: missingExcise,
                                }),
                                gridTemplateColumns: cols,
                                gap: 10,
                                padding: '14px 18px',
                            }}
                            onClick={() => !bulkMode && onOpen('stateProduct', row._id)}
                        >
                            <div onClick={(e) => e.stopPropagation()}>
                                {!bulkMode ? (
                                    <CpmCheckbox
                                        checked={selected}
                                        onChange={() => onToggle(row)}
                                    />
                                ) : (
                                    <span />
                                )}
                            </div>
                            <span className="text-[12.5px] font-extrabold text-[var(--cpm-text-primary)] truncate">
                                {row.state}
                            </span>
                            {!bulkMode &&
                            (row.variantInfo?._id || row.productVariant) &&
                            row.variantInfo?.name ? (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpen(
                                            'variant',
                                            String(row.variantInfo?._id ?? row.productVariant)
                                        );
                                    }}
                                    className="block min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer text-[13px] font-semibold tracking-[-0.005em] leading-[1.35] text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere] underline decoration-[var(--cpm-border-strong)] underline-offset-[3px] hover:text-[var(--cpm-brand-purple)]"
                                >
                                    {row.variantInfo.name}
                                </button>
                            ) : (
                                <span className="min-w-0 text-[12.5px] font-bold text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere]">
                                    {row.variantInfo?.name ?? '—'}
                                </span>
                            )}
                            {missingExcise && !bulkMode ? (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpen('stateProduct', row._id);
                                    }}
                                    className="block min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer text-[12.5px] font-bold truncate text-[var(--cpm-warning-text)] hover:underline"
                                >
                                    Not curated
                                </button>
                            ) : (
                                <span
                                    className={[
                                        'text-[12.5px] font-bold truncate',
                                        missingExcise
                                            ? 'text-[var(--cpm-warning-text)]'
                                            : 'text-[var(--cpm-text-secondary)]',
                                    ].join(' ')}
                                >
                                    {row.exciseCategoryName || 'Not curated'}
                                </span>
                            )}
                            <span
                                className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] truncate"
                                title={
                                    bulkMode
                                        ? 'Edit subcategory via the record form (ObjectId picker)'
                                        : undefined
                                }
                            >
                                {row.exciseSubCategoryName || '—'}
                            </span>
                            <div className="min-w-0">
                                {bulkMode ? (
                                    <CpmFilterDropdown
                                        label=""
                                        options={cpmLicenseTypeOptions(
                                            rowLicenseTypes,
                                            licenseValues
                                        )}
                                        selected={licenseValues}
                                        onChange={(vals) => onBulkField(key, 'licenseType', vals)}
                                        multi
                                        disabled={rowLicenseDisabled}
                                        includeEmptyOption={false}
                                        emptyLabel="None yet"
                                        className={[
                                            '!flex-none !min-w-0 !max-w-none w-full [&>span:first-child]:hidden [&>button]:!h-[34px] [&>button]:!rounded-[8px] [&>button_span]:!text-[12.5px] [&>button_span]:!font-bold',
                                            'licenseType' in draft
                                                ? '[&>button]:!border-[var(--cpm-brand-purple)] [&>button]:!bg-[rgba(110,100,240,.10)] [&>button]:!shadow-[0_0_0_2px_rgba(110,100,240,.16)]'
                                                : '[&>button]:!border-[var(--cpm-border-strong)] [&>button]:!bg-white [&>button]:!shadow-none',
                                        ].join(' ')}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] truncate">
                                        {(row.licenseType ?? []).join(', ') || '—'}
                                    </span>
                                )}
                            </div>
                            <div className="text-right">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(
                                            draft.purchasePrice ?? row.purchasePrice ?? ''
                                        )}
                                        onChange={(v) =>
                                            onBulkField(key, 'purchasePrice', Number(v))
                                        }
                                        changed={'purchasePrice' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {formatMoney(row.purchasePrice)}
                                    </span>
                                )}
                            </div>
                            <div className="text-right">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(draft.retailPrice ?? row.retailPrice ?? '')}
                                        onChange={(v) => onBulkField(key, 'retailPrice', Number(v))}
                                        changed={'retailPrice' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {formatMoney(row.retailPrice)}
                                    </span>
                                )}
                            </div>
                            <div className="text-right">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(
                                            draft.wholesalePrice ?? row.wholesalePrice ?? ''
                                        )}
                                        onChange={(v) =>
                                            onBulkField(key, 'wholesalePrice', Number(v))
                                        }
                                        changed={'wholesalePrice' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {formatMoney(row.wholesalePrice)}
                                    </span>
                                )}
                            </div>
                            <div className="text-right">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(
                                            draft.distilleryPrice ?? row.distilleryPrice ?? ''
                                        )}
                                        onChange={(v) =>
                                            onBulkField(key, 'distilleryPrice', Number(v))
                                        }
                                        changed={'distilleryPrice' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {formatMoney(row.distilleryPrice)}
                                    </span>
                                )}
                            </div>
                            <CpmStatusChip status={row.status} />
                            <RowActions
                                show={!bulkMode}
                                approveState={resolveApproveState(row.status)}
                                onApprove={() =>
                                    onApprove(
                                        'stateProduct',
                                        row._id,
                                        row.variantInfo?.name ?? row.state,
                                        row.status
                                    )
                                }
                                onEdit={() => onOpen('stateProduct', row._id)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    if (catalogView === 'variants' && !combined) {
        const rows = sortedItems as CpmVariantRecord[];
        const cols = bulkMode
            ? 'minmax(210px,1.4fr) minmax(160px,1fr) 92px 132px 84px minmax(190px,1.1fr) minmax(190px,1.15fr) 118px'
            : '40px minmax(210px,1.4fr) minmax(160px,1fr) 92px 104px 84px minmax(180px,1.2fr) 118px 118px 152px';
        return (
            <div style={{ minWidth: 1420 }}>
                <div
                    className={headClass}
                    style={{
                        gridTemplateColumns: cols,
                        gap: 10,
                        padding: '11px 18px',
                        ...headRowTypeStyle,
                    }}
                >
                    {!bulkMode ? (
                        <CpmCheckbox
                            checked={allSelected}
                            onChange={(e) => onToggleAll(e.target.checked)}
                        />
                    ) : null}
                    <SortableHead
                        label="Name"
                        sortKey="name"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Product · Brand"
                        sortKey="product"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Volume"
                        sortKey="volume"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Container"
                        sortKey="container"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Per case"
                        sortKey="itemPerCase"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className="justify-end w-full"
                    />
                    <SortableHead
                        label="Barcodes / GTINs"
                        sortKey="barcodes"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    <SortableHead
                        label="Aliases"
                        sortKey="aliases"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className={bulkMode ? 'w-full' : 'justify-end w-full'}
                    />
                    <SortableHead
                        label="Status"
                        sortKey="status"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                    />
                    {!bulkMode ? <span /> : null}
                </div>
                {rows.map((row) => {
                    const selected = selectedIds.includes(row._id);
                    const key = draftKey('variant', row._id);
                    const draft = bulkDrafts[key] ?? {};
                    const edited = isEdited(bulkMode, bulkDrafts, key);
                    const productId = row.productInfo?._id ?? row.alcoholProduct;
                    const productName = row.productInfo?.name;
                    const brandName = row.brandInfo?.name;
                    const aliases = bulkMode
                        ? ((draft.aliases as string[] | undefined) ?? row.aliases ?? [])
                        : (row.aliases ?? []);
                    const containerValue = String(
                        draft.containerType ??
                            row.containerType ??
                            CPM_CONTAINER_TYPE_OPTIONS[0]?.value ??
                            ''
                    );
                    return (
                        <div
                            key={row._id}
                            className="grid items-center cursor-pointer"
                            style={{
                                ...rowSurfaceStyle({ selected, bulkMode, edited }),
                                gridTemplateColumns: cols,
                                gap: 10,
                                padding: '14px 18px',
                            }}
                            onClick={() => !bulkMode && onOpen('variant', row._id)}
                        >
                            {!bulkMode ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <CpmCheckbox
                                        checked={selected}
                                        onChange={() => onToggle(row)}
                                    />
                                </div>
                            ) : null}
                            <div className="min-w-0 overflow-hidden">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(draft.name ?? row.name)}
                                        onChange={(v) => onBulkField(key, 'name', v)}
                                        changed={'name' in draft}
                                    />
                                ) : (
                                    <span className="text-[13px] font-extrabold text-[var(--cpm-text-primary)] break-words [overflow-wrap:anywhere]">
                                        {row.name}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col gap-[3px] min-w-0">
                                {productId && productName ? (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpen('product', String(productId));
                                        }}
                                        className="block text-left bg-transparent border-0 p-0 cursor-pointer text-[13px] font-semibold tracking-[-0.005em] leading-[1.35] text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere] underline decoration-[var(--cpm-border-strong)] underline-offset-[3px] hover:text-[var(--cpm-brand-purple)]"
                                    >
                                        {productName}
                                    </button>
                                ) : (
                                    <span className="text-[13px] font-semibold tracking-[-0.005em] leading-[1.35] text-[var(--cpm-text-secondary)]">
                                        {productName || '—'}
                                    </span>
                                )}
                                {brandName ? (
                                    <span className="text-[11px] font-semibold leading-[1.3] text-[var(--cpm-text-tertiary)] break-words [overflow-wrap:anywhere]">
                                        {brandName}
                                    </span>
                                ) : null}
                            </div>
                            <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                {formatVolumeLabel(row.volumeMl, row.volumeUnit)}
                            </span>
                            <div className="min-w-0">
                                {bulkMode ? (
                                    <InlineEnumCell
                                        value={containerValue}
                                        options={CPM_CONTAINER_TYPE_OPTIONS}
                                        onChange={(v) => onBulkField(key, 'containerType', v)}
                                        changed={'containerType' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] truncate">
                                        {row.containerType || '—'}
                                    </span>
                                )}
                            </div>
                            <div className="text-right">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(draft.itemPerCase ?? row.itemPerCase ?? '')}
                                        onChange={(v) => onBulkField(key, 'itemPerCase', Number(v))}
                                        changed={'itemPerCase' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {row.itemPerCase ?? '—'}
                                    </span>
                                )}
                            </div>
                            {bulkMode ? (
                                <InlineChipCell
                                    values={(draft.gtin as string[] | undefined) ?? row.gtin ?? []}
                                    onChange={(v) => onBulkField(key, 'gtin', v)}
                                    placeholder="GTIN, then Enter"
                                    addLabel="Add"
                                    emptyLabel="No GTIN"
                                    changed={'gtin' in draft}
                                />
                            ) : (
                                <GtinChips values={row.gtin} />
                            )}
                            <div className={bulkMode ? 'min-w-0' : 'min-w-0 text-right'}>
                                {bulkMode ? (
                                    <InlineChipCell
                                        values={aliases}
                                        onChange={(v) => onBulkField(key, 'aliases', v)}
                                        placeholder="Alias, then Enter"
                                        addLabel="Add"
                                        emptyLabel="No aliases"
                                        changed={'aliases' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular whitespace-nowrap">
                                        {formatAliasCountLabel(row.aliases?.length ?? 0)}
                                    </span>
                                )}
                            </div>
                            <CpmStatusChip status={row.status} />
                            {!bulkMode ? (
                                <RowActions
                                    show
                                    approveState={resolveApproveState(
                                        row.status,
                                        row.productInfo?.status
                                    )}
                                    onApprove={() =>
                                        onApprove('variant', row._id, row.name, row.status)
                                    }
                                    onEdit={() => onOpen('variant', row._id)}
                                />
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
    }

    const rows = sortedItems as CpmProductRecord[];
    // Combined view: parent + child must share identical trailing Status + Actions
    // tracks so those columns share one vertical axis (Approve+Edit needs 178px).
    const COMBINED_STATUS_W = '118px';
    const COMBINED_ACTIONS_W = '178px';
    // Design products cols: name, aliases(130), brand, variants(120), abv(96), status
    const productCols = combined
        ? bulkMode
            ? `minmax(250px,1.5fr) minmax(230px,1.5fr) minmax(160px,1fr) 96px ${COMBINED_STATUS_W}`
            : `40px minmax(250px,1.5fr) 130px minmax(160px,1fr) 96px ${COMBINED_STATUS_W} ${COMBINED_ACTIONS_W}`
        : bulkMode
          ? 'minmax(250px,1.5fr) minmax(230px,1.5fr) minmax(160px,1fr) 120px 96px 136px'
          : '40px minmax(250px,1.5fr) 130px minmax(160px,1fr) 120px 96px 136px 152px';
    // Design childBase: variant, volume, container, items/case, barcodes, aliases, status
    const childCols = bulkMode
        ? `14px minmax(140px,0.8fr) 88px 100px 92px minmax(180px,1.05fr) minmax(160px,1fr) ${COMBINED_STATUS_W}`
        : `40px 14px minmax(190px,1.4fr) 88px 104px 92px minmax(190px,1.1fr) minmax(150px,1fr) ${COMBINED_STATUS_W} ${COMBINED_ACTIONS_W}`;

    return (
        <div style={{ minWidth: combined ? 1480 : 1380 }}>
            <div
                className={headClass}
                style={{
                    gridTemplateColumns: productCols,
                    gap: 10,
                    padding: '11px 18px',
                    ...headRowTypeStyle,
                }}
            >
                {!bulkMode ? (
                    <CpmCheckbox
                        checked={allSelected}
                        onChange={(e) => onToggleAll(e.target.checked)}
                    />
                ) : null}
                <SortableHead
                    label="Name"
                    sortKey="name"
                    activeKey={sortBy}
                    dir={sortDir}
                    onSort={onSort}
                />
                <SortableHead
                    label="Aliases"
                    sortKey="aliases"
                    activeKey={sortBy}
                    dir={sortDir}
                    onSort={onSort}
                    className={bulkMode ? 'w-full' : 'justify-end w-full'}
                />
                <SortableHead
                    label="Brand"
                    sortKey="brand"
                    activeKey={sortBy}
                    dir={sortDir}
                    onSort={onSort}
                />
                {!combined ? (
                    <SortableHead
                        label="Variants"
                        sortKey="variants"
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={onSort}
                        className="justify-center w-full"
                    />
                ) : null}
                <SortableHead
                    label="Alcohol content"
                    sortKey="alcoholContent"
                    activeKey={sortBy}
                    dir={sortDir}
                    onSort={onSort}
                    className="justify-center w-full"
                />
                <SortableHead
                    label="Status"
                    sortKey="status"
                    activeKey={sortBy}
                    dir={sortDir}
                    onSort={onSort}
                />
                {!bulkMode ? <span /> : null}
            </div>
            {rows.map((row) => {
                const selected = selectedIds.includes(row._id);
                const key = draftKey('product', row._id);
                const draft = bulkDrafts[key] ?? {};
                const edited = isEdited(bulkMode, bulkDrafts, key);
                const expanded = combined && !collapsedIds.includes(row._id);
                const variantCount = row.variants?.length ?? row.variantCount ?? 0;
                const sortedVariants = sortVariants(row.variants ?? [], childSortKey, childSortDir);
                const aliases = bulkMode
                    ? ((draft.aliases as string[] | undefined) ?? row.aliases ?? [])
                    : (row.aliases ?? []);
                return (
                    <div key={row._id}>
                        <div
                            className="grid items-center cursor-pointer"
                            style={{
                                ...rowSurfaceStyle({ selected, bulkMode, edited }),
                                gridTemplateColumns: productCols,
                                gap: 10,
                                padding: '14px 18px',
                            }}
                            onClick={() => !bulkMode && onOpen('product', row._id)}
                        >
                            {!bulkMode ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <CpmCheckbox
                                        checked={selected}
                                        onChange={() => onToggle(row)}
                                    />
                                </div>
                            ) : null}
                            <div className="flex items-center gap-[9px] min-w-0">
                                {combined ? (
                                    <ExpandCaret
                                        expanded={expanded}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleExpand(row._id);
                                        }}
                                    />
                                ) : null}
                                {bulkMode ? (
                                    <InlineText
                                        value={String(draft.name ?? row.name)}
                                        onChange={(v) => onBulkField(key, 'name', v)}
                                        changed={'name' in draft}
                                    />
                                ) : (
                                    <span className="text-[13.5px] font-extrabold tracking-[-0.01em] text-[var(--cpm-text-primary)] break-words [overflow-wrap:anywhere]">
                                        {row.name}
                                    </span>
                                )}
                            </div>
                            <div className={bulkMode ? 'min-w-0' : 'min-w-0 text-right'}>
                                {bulkMode ? (
                                    <InlineChipCell
                                        values={aliases}
                                        onChange={(v) => onBulkField(key, 'aliases', v)}
                                        placeholder="Alias, then Enter"
                                        addLabel="Add"
                                        emptyLabel="No aliases"
                                        changed={'aliases' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular whitespace-nowrap">
                                        {formatAliasCountLabel(row.aliases?.length ?? 0)}
                                    </span>
                                )}
                            </div>
                            {!combined && !bulkMode && row.brandInfo?._id && row.brandInfo?.name ? (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpen('brand', String(row.brandInfo!._id));
                                    }}
                                    className="block min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer text-[13px] font-semibold tracking-[-0.005em] leading-[1.35] text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere] underline decoration-[var(--cpm-border-strong)] underline-offset-[3px] hover:text-[var(--cpm-brand-purple)]"
                                >
                                    {row.brandInfo.name}
                                </button>
                            ) : (
                                <span className="min-w-0 text-[12.5px] font-bold text-[var(--cpm-text-secondary)] break-words [overflow-wrap:anywhere]">
                                    {row.brandInfo?.name ?? '—'}
                                </span>
                            )}
                            {!combined ? (
                                <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular text-center">
                                    {formatCpmCount(variantCount)}
                                </span>
                            ) : null}
                            <div className="text-center">
                                {bulkMode ? (
                                    <InlineText
                                        value={String(
                                            draft.alcoholContent ?? row.alcoholContent ?? ''
                                        )}
                                        onChange={(v) =>
                                            onBulkField(key, 'alcoholContent', Number(v))
                                        }
                                        changed={'alcoholContent' in draft}
                                    />
                                ) : (
                                    <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                        {row.alcoholContent != null
                                            ? `${row.alcoholContent}%`
                                            : '—'}
                                    </span>
                                )}
                            </div>
                            <CpmStatusChip status={row.status} />
                            {!bulkMode ? (
                                <RowActions
                                    show
                                    approveState={resolveApproveState(
                                        row.status,
                                        row.brandInfo?.status
                                    )}
                                    onApprove={() =>
                                        onApprove('product', row._id, row.name, row.status)
                                    }
                                    onEdit={() => onOpen('product', row._id)}
                                />
                            ) : null}
                        </div>

                        {combined && expanded ? (
                            variantCount === 0 ? (
                                <div
                                    className="flex items-center gap-[10px]"
                                    style={{
                                        padding: '11px 18px 11px 76px',
                                        ...rowSurfaceStyle({
                                            selected: false,
                                            bulkMode,
                                            edited: false,
                                        }),
                                    }}
                                >
                                    <span className="text-[12px] font-bold text-[var(--cpm-text-tertiary)]">
                                        No variants under this product yet.
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => onCreateVariant(row._id, row.name)}
                                        className="text-[12px] font-extrabold text-[var(--cpm-brand-purple)] border-b border-[var(--cpm-brand-purple)]"
                                    >
                                        Create a variant
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="cpm-table-head cpm-table-head-child grid items-center"
                                        style={{
                                            gridTemplateColumns: childCols,
                                            gap: 10,
                                            padding: '9px 18px 9px 34px',
                                            fontSize: 9.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                            color: TH_COLOR,
                                            fontFamily:
                                                "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                                            background: '#fff',
                                            borderBottom: '1px solid var(--cpm-border-default)',
                                        }}
                                    >
                                        {!bulkMode ? <span /> : null}
                                        <span />
                                        <SortableHead
                                            label="Variant"
                                            sortKey="name"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                        />
                                        <SortableHead
                                            label="Volume"
                                            sortKey="volume"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                        />
                                        <SortableHead
                                            label="Container"
                                            sortKey="container"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                        />
                                        <SortableHead
                                            label="Items / case"
                                            sortKey="itemPerCase"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                        />
                                        <SortableHead
                                            label="Barcodes"
                                            sortKey="barcodes"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                        />
                                        <SortableHead
                                            label="Aliases"
                                            sortKey="aliases"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                            className={bulkMode ? 'w-full' : 'justify-end w-full'}
                                        />
                                        <SortableHead
                                            label="Status"
                                            sortKey="status"
                                            activeKey={childSortKey}
                                            dir={childSortDir}
                                            onSort={onChildSort}
                                            child
                                        />
                                        {!bulkMode ? <span /> : null}
                                    </div>
                                    {sortedVariants.map((ch) => {
                                        const chSelected = selectedIds.includes(ch._id);
                                        const chKey = draftKey('variant', ch._id);
                                        const chDraft = bulkDrafts[chKey] ?? {};
                                        const chEdited = isEdited(bulkMode, bulkDrafts, chKey);
                                        const chGtin = bulkMode
                                            ? ((chDraft.gtin as string[] | undefined) ??
                                              ch.gtin ??
                                              [])
                                            : (ch.gtin ?? []);
                                        const chAliases = bulkMode
                                            ? ((chDraft.aliases as string[] | undefined) ??
                                              ch.aliases ??
                                              [])
                                            : (ch.aliases ?? []);
                                        const chContainer = String(
                                            chDraft.containerType ??
                                                ch.containerType ??
                                                CPM_CONTAINER_TYPE_OPTIONS[0]?.value ??
                                                ''
                                        );
                                        return (
                                            <div
                                                key={ch._id}
                                                className="grid items-center cursor-pointer"
                                                style={{
                                                    ...rowSurfaceStyle({
                                                        selected: chSelected,
                                                        bulkMode,
                                                        edited: chEdited,
                                                        nested: true,
                                                    }),
                                                    gridTemplateColumns: childCols,
                                                    gap: 10,
                                                    padding: '10px 18px 10px 34px',
                                                }}
                                                onClick={() =>
                                                    !bulkMode && onOpen('variant', ch._id)
                                                }
                                            >
                                                {!bulkMode ? (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <CpmCheckbox
                                                            checked={chSelected}
                                                            onChange={() => onToggle(ch, true)}
                                                        />
                                                    </div>
                                                ) : null}
                                                <span
                                                    className="justify-self-center"
                                                    style={{
                                                        width: 14,
                                                        height: 1.5,
                                                        background: 'var(--cpm-border-strong)',
                                                    }}
                                                />
                                                <div className="flex flex-col gap-[3px] min-w-0">
                                                    {bulkMode ? (
                                                        <InlineText
                                                            value={String(chDraft.name ?? ch.name)}
                                                            onChange={(v) =>
                                                                onBulkField(chKey, 'name', v)
                                                            }
                                                            changed={'name' in chDraft}
                                                        />
                                                    ) : (
                                                        <>
                                                            <span className="text-[12.5px] font-extrabold tracking-[-0.005em] text-[var(--cpm-text-primary)] break-words [overflow-wrap:anywhere]">
                                                                {ch.name}
                                                            </span>
                                                            <span
                                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                                style={{
                                                                    fontSize: 10,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.06em',
                                                                }}
                                                            >
                                                                Variant
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                                <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                                    {formatVolumeLabel(ch.volumeMl, ch.volumeUnit)}
                                                </span>
                                                <div className="min-w-0">
                                                    {bulkMode ? (
                                                        <InlineEnumCell
                                                            value={chContainer}
                                                            options={CPM_CONTAINER_TYPE_OPTIONS}
                                                            onChange={(v) =>
                                                                onBulkField(
                                                                    chKey,
                                                                    'containerType',
                                                                    v
                                                                )
                                                            }
                                                            changed={'containerType' in chDraft}
                                                        />
                                                    ) : (
                                                        <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] truncate">
                                                            {ch.containerType || '—'}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular">
                                                    {ch.itemPerCase ?? '—'}
                                                </span>
                                                {bulkMode ? (
                                                    <InlineChipCell
                                                        values={chGtin}
                                                        onChange={(v) =>
                                                            onBulkField(chKey, 'gtin', v)
                                                        }
                                                        placeholder="Add GTIN"
                                                        addLabel="Add"
                                                        changed={'gtin' in chDraft}
                                                        emptyLabel="No GTIN"
                                                    />
                                                ) : (
                                                    <GtinChips values={ch.gtin} />
                                                )}
                                                <div
                                                    className={
                                                        bulkMode ? 'min-w-0' : 'min-w-0 text-right'
                                                    }
                                                >
                                                    {bulkMode ? (
                                                        <InlineChipCell
                                                            values={chAliases}
                                                            onChange={(v) =>
                                                                onBulkField(chKey, 'aliases', v)
                                                            }
                                                            placeholder="Alias, then Enter"
                                                            addLabel="Add"
                                                            emptyLabel="No aliases"
                                                            changed={'aliases' in chDraft}
                                                        />
                                                    ) : (
                                                        <span className="text-[12.5px] font-bold text-[var(--cpm-text-secondary)] tabular whitespace-nowrap">
                                                            {formatAliasCountLabel(
                                                                ch.aliases?.length ?? 0
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                <CpmStatusChip status={ch.status} />
                                                {!bulkMode ? (
                                                    <RowActions
                                                        show
                                                        approveState={resolveApproveState(
                                                            ch.status,
                                                            row.status
                                                        )}
                                                        onApprove={() =>
                                                            onApprove(
                                                                'variant',
                                                                ch._id,
                                                                ch.name,
                                                                ch.status
                                                            )
                                                        }
                                                        onEdit={() => onOpen('variant', ch._id)}
                                                    />
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </>
                            )
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
